/**
 * Pure helpers for custom MCP servers on the Integrations page: labels the
 * rows and the detail page share, and the add/edit form's checks. The
 * workspace server re-checks everything; these only spare the admin a round
 * trip for what is plainly incomplete.
 *
 * @module components/apps/integrations/customMcpServerContent
 */
import type {
  CustomMcpAuthType,
  CustomMcpConnectionInput,
  CustomMcpScope,
  CustomMcpServer,
  CustomMcpTool,
} from "../../../state/customMcpServers";

export const CUSTOM_MCP_NAME_MAX = 80;
export const CUSTOM_MCP_HEADER_NAME_MAX = 100;

export const CUSTOM_MCP_AUTH_OPTIONS: ReadonlyArray<{
  readonly id: CustomMcpAuthType;
  readonly label: string;
  readonly description: string;
}> = [
  { id: "none", label: "None", description: "The server needs no credential." },
  {
    id: "bearer",
    label: "Bearer token",
    description: "Sent as Authorization: Bearer <token>.",
  },
  {
    id: "header",
    label: "Custom header",
    description: "Sent under a header name the server expects.",
  },
];

export const customMcpAuthLabel = (authType: CustomMcpAuthType): string =>
  CUSTOM_MCP_AUTH_OPTIONS.find((option) => option.id === authType)?.label ?? authType;

/** Who can use a server, as its page says it. */
export const customMcpScopeLabel = (scope: CustomMcpScope): string =>
  scope === "personal" ? "Only you" : "Everyone in the organization";

/** The badge a server carries among the plugins. */
export const customMcpScopeBadge = (scope: CustomMcpScope): string =>
  scope === "personal" ? "Personal" : "Organization";

/**
 * The "Who can use it" choices a person gets when connecting a server.
 * Integrations adds organization servers only, so only an admin gets any
 * choice there (and no button otherwise); Plugins offers every member a
 * personal server first, and an admin the organization too.
 */
export function customMcpScopeOptions(input: {
  readonly defaultScope: CustomMcpScope;
  readonly canManageOrganization: boolean;
  readonly organizationName: string | null;
}): ReadonlyArray<{ readonly id: CustomMcpScope; readonly label: string }> {
  const organization = {
    id: "organization" as const,
    label: `Everyone in ${input.organizationName?.trim() || "the organization"}`,
  };
  if (input.defaultScope === "organization") {
    return input.canManageOrganization ? [organization] : [];
  }
  return [
    { id: "personal" as const, label: "Only me" },
    ...(input.canManageOrganization ? [organization] : []),
  ];
}


/** The servers Compliance → Integrations lists: the organization's, never anyone's personal ones. */
export const organizationCustomMcpServers = (
  servers: ReadonlyArray<CustomMcpServer>,
): ReadonlyArray<CustomMcpServer> => servers.filter((server) => server.scope === "organization");

/** The host a server lives at, for a row's second line; the raw value when it is not a URL. */
export function customMcpHost(url: string): string {
  try {
    return new URL(url).host || url;
  } catch {
    return url;
  }
}

/** "3 of 5 tools allowed"; "No tools" when the server offers none. */
export function allowedToolsLabel(tools: ReadonlyArray<Pick<CustomMcpTool, "allowed">>): string {
  if (tools.length === 0) return "No tools";
  const allowed = tools.filter((tool) => tool.allowed).length;
  return `${allowed} of ${tools.length} ${tools.length === 1 ? "tool" : "tools"} allowed`;
}

/** The names of the tools allowed after ticking (or unticking) one. */
export function toggleAllowedTool(
  allowed: ReadonlyArray<string>,
  name: string,
  next: boolean,
): ReadonlyArray<string> {
  const without = allowed.filter((entry) => entry !== name);
  return next ? [...without, name] : without;
}

/** Whether two allowed sets differ, ignoring order. */
export const allowedToolsChanged = (
  before: ReadonlyArray<string>,
  after: ReadonlyArray<string>,
): boolean => [...before].sort().join("\n") !== [...after].sort().join("\n");

/** The add/edit form, as typed. */
export interface CustomMcpForm {
  readonly name: string;
  readonly url: string;
  readonly authType: CustomMcpAuthType;
  readonly headerName: string;
  readonly secret: string;
  /** Chosen when adding; a server keeps its scope for life. */
  readonly scope: CustomMcpScope;
}

export const EMPTY_CUSTOM_MCP_FORM: CustomMcpForm = {
  name: "",
  url: "",
  authType: "none",
  headerName: "",
  secret: "",
  scope: "organization",
};

/** The edit form starts from what is stored — except the secret, which never comes back. */
export const customMcpFormFor = (server: CustomMcpServer): CustomMcpForm => ({
  name: server.name,
  url: server.url,
  authType: server.authType,
  headerName: server.headerName ?? "",
  secret: "",
  scope: server.scope,
});

export type CustomMcpFormField = keyof CustomMcpForm;

/** HTTP token characters (RFC 9110 §5.6.2). */
const HEADER_TOKEN = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;

/**
 * Whether a stored secret can carry over: only when the server keeps the
 * same kind of credential and has one.
 */
export const keepsStoredSecret = (
  stored: Pick<CustomMcpServer, "authType" | "hasSecret"> | null,
  authType: CustomMcpAuthType,
): boolean =>
  stored !== null && authType !== "none" && stored.authType === authType && stored.hasSecret;

/**
 * The first thing wrong with the form, or null when it can be sent. `stored`
 * is the server being edited (null when adding).
 */
export function validateCustomMcpForm(
  form: CustomMcpForm,
  stored: Pick<CustomMcpServer, "authType" | "hasSecret"> | null,
): { readonly field: CustomMcpFormField; readonly message: string } | null {
  const name = form.name.trim();
  if (name.length === 0) return { field: "name", message: "Enter a name." };
  if (name.length > CUSTOM_MCP_NAME_MAX) {
    return { field: "name", message: `Keep the name to ${CUSTOM_MCP_NAME_MAX} characters.` };
  }
  const url = form.url.trim();
  if (url.length === 0) return { field: "url", message: "Enter the server URL." };
  let parsed: URL | null = null;
  try {
    parsed = new URL(url);
  } catch {
    parsed = null;
  }
  if (parsed === null || parsed.protocol !== "https:" || parsed.hostname.length === 0) {
    return { field: "url", message: "Enter an https:// URL." };
  }
  if (form.authType === "header") {
    const headerName = form.headerName.trim();
    if (headerName.length === 0) return { field: "headerName", message: "Enter the header name." };
    if (headerName.length > CUSTOM_MCP_HEADER_NAME_MAX || !HEADER_TOKEN.test(headerName)) {
      return {
        field: "headerName",
        message: "Use letters, digits and dashes only in the header name.",
      };
    }
  }
  if (
    form.authType !== "none" &&
    form.secret.length === 0 &&
    !keepsStoredSecret(stored, form.authType)
  ) {
    return {
      field: "secret",
      message: form.authType === "bearer" ? "Enter the token." : "Enter the header value.",
    };
  }
  return null;
}

/** The connection the form describes; a blank secret means "keep the stored one". */
export const connectionFromForm = (form: CustomMcpForm): CustomMcpConnectionInput => ({
  url: form.url.trim(),
  authType: form.authType,
  headerName: form.authType === "header" ? form.headerName.trim() : null,
  secret: form.authType === "none" || form.secret.length === 0 ? null : form.secret,
});
